import { useEffect, useRef, useState } from "react";
import { useNavigate, useOutletContext } from "react-router";
import { useSearchParams } from "react-router-dom";
import Logo from "../../sharable/Logo";
import Button from "../../sharable/ActionButton";
import {
  Container,
  PaymentInfo,
  CpfCnpjWrapper,
  CpfCnpjInput,
} from "./PixCheckout.styles";
import Help from "../../sharable/Help";
import Loading from "../../sharable/Loading";
import Notification from "../../sharable/Notification";
import { formatPlan, formatPlanPrice } from "../../../services/Utils";
import useFormValidation from "../../sharable/hooks/useFormValidation";
import QRCode from "react-qr-code";

const REACT_APP_SERVER_BASE_URL =
  process.env.REACT_APP_SERVER_BASE_URL || "http://localhost:8080/api/";

const PIX_SESSION_KEY = "pixCheckoutSession";
const PIX_SESSION_TTL = 30 * 60 * 1000; // 30 minutes

function formatCpfCnpj(value) {
  value = value.replace(/\D/g, "");
  if (value.length <= 11) {
    value = value.replace(/(\d{3})(\d)/, "$1.$2");
    value = value.replace(/(\d{3})(\d)/, "$1.$2");
    value = value.replace(/(\d{3})(\d{1,2})$/, "$1-$2");
  } else {
    value = value.replace(/^(\d{2})(\d)/, "$1.$2");
    value = value.replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3");
    value = value.replace(/\.(\d{3})(\d)/, ".$1/$2");
    value = value.replace(/(\d{4})(\d{1,2})$/, "$1-$2");
  }
  return value;
}

function cpfCnpjToRawValue(value) {
  value = value.replace(".", "");
  value = value.replace("/", "");
  value = value.replace(".", "");
  value = value.replace("-", "");
  return value;
}
const cpfCnpjRegex =
  /(^\d{3}\.\d{3}\.\d{3}-\d{2}$)|(^\d{2}\.\d{3}\.\d{3}\/\d{4}-\d{2}$)/;

export default function PixCheckout() {
  const { token, user } = useOutletContext();
  const [params] = useSearchParams();
  const planLookupKey = params.get("lookup_key");
  const planPrice = formatPlanPrice(planLookupKey);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [requestError, setRequestError] = useState(null);
  const pollingRef = useRef(null);
  const [pixDetails, setPixDetails] = useState(null);

  const form = useFormValidation({
    cpfCnpj: {
      label: "CPF ou CNPJ",
      required: true,
      regex: cpfCnpjRegex,
      maxLength: 18,
      customMessages: {
        regex: "Digite um CPF ou CNPJ válido.",
        required: "Campo obrigatório.",
        maxLength: "Deve ter no máximo 18 dígitos.",
      },
    },
  });

  const [copied, setCopied] = useState(false);
  const navigate = useNavigate();
  useEffect(() => {
    if (!planLookupKey || planPrice === "-") {
      navigate("/planos");
      return;
    }
  }, [planLookupKey, planPrice, navigate]);

  // Restore PIX session from localStorage on mount
  useEffect(() => {
    const restore = async () => {
      try {
        const raw = localStorage.getItem(PIX_SESSION_KEY);
        if (!raw) return;
        const stored = JSON.parse(raw);
        if (
          !stored ||
          stored.exp <= Date.now() ||
          stored.lookupKey !== planLookupKey
        ) {
          localStorage.removeItem(PIX_SESSION_KEY);
          return;
        }
        const res = await fetch(
          `${REACT_APP_SERVER_BASE_URL}subscription/order-status?txid=${stored.txid}`,
          { headers: { Authorization: `${token}` } },
        );
        if (!res.ok) {
          localStorage.removeItem(PIX_SESSION_KEY);
          return;
        }
        const payment = await res.json();
        if (payment.status === "PENDING") {
          setPixDetails(payment);
          setPaymentStatus("PENDING");
        } else {
          localStorage.removeItem(PIX_SESSION_KEY);
          if (payment.status === "COMPLETED")
            setTimeout(() => (window.location = "/oferta-especial"), 4000);
        }
      } catch {
        localStorage.removeItem(PIX_SESSION_KEY);
      }
    };
    restore();
  }, []);

  const generatePix = async () => {
    setRequestError(null);

    if (form.validate() === false) return;

    const res = await fetch(`${REACT_APP_SERVER_BASE_URL}subscription/pix`, {
      method: "POST",
      headers: {
        Authorization: `${token}`,
        "Content-Type": "application/json",
      },

      body: JSON.stringify({
        payerEmail: user.email,
        subscriptionLookupKey: `${planLookupKey}`,
        cpfCnpj: cpfCnpjToRawValue(form.values.cpfCnpj),
      }),
    });

    if (res.ok) {
      const json = await res.json();
      setPaymentStatus("PENDING");
      setPixDetails(json);
      localStorage.setItem(
        PIX_SESSION_KEY,
        JSON.stringify({
          txid: json.txid,
          lookupKey: planLookupKey,
          exp: Date.now() + PIX_SESSION_TTL,
        }),
      );
    } else if (res.status === 429) {
      setRequestError("Aguarde antes de solicitar outro código.");
    }
  };

  const copyPixCode = () => {
    if (pixDetails?.copyPasteCode) {
      navigator.clipboard.writeText(pixDetails.copyPasteCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  useEffect(() => {
    if (pixDetails && paymentStatus === "PENDING") {
      const paymentExternalReference = pixDetails.txid;
      pollingRef.current = setInterval(async () => {
        try {
          const res = await fetch(
            `${REACT_APP_SERVER_BASE_URL}subscription/order-status?txid=${paymentExternalReference}`,
            {
              headers: {
                Authorization: `${token}`,
                "Content-Type": "application/json",
              },
            },
          );
          const payment = await res.json();
          console.log(payment);
          setPaymentStatus(payment.status);

          if (payment.status === "COMPLETED") {
            localStorage.removeItem(PIX_SESSION_KEY);
            setTimeout(() => (window.location = "/oferta-especial"), 4000);
          }
          if (payment.status !== "PENDING") {
            localStorage.removeItem(PIX_SESSION_KEY);
            clearInterval(pollingRef.current);
          }
        } catch (err) {
          console.error(err);
        }
      }, 5000);
    }
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, [pixDetails, paymentStatus, token]);

  return (
    <Container>
      <header>
        <Logo showGoBackButton={true} path={"/planos"} className="logo" />
      </header>
      <main className="flex direction-column align">
        {requestError && <Notification message={requestError} type={"error"} />}
        {paymentStatus === "COMPLETED" && (
          <Notification message={"Pagamento aprovado"} type={"success"} />
        )}

        {paymentStatus === "PENDING" && (
          <Notification message={"Aguardando transferência"} type={"info"} />
        )}
        <PaymentInfo className="flex justify direction-column align">
          <h1>Pagar via Pix</h1>
          <div className="flex plan-price-wrapper">
            <div className="flex direction-column r">
              <span className="plan">{formatPlan(planLookupKey)}</span>
              <span className="alter-plan" onClick={() => navigate("/planos")}>
                Alterar plano
              </span>
            </div>

            <span className="price">
              R$
              {planPrice}
            </span>
          </div>
          <CpfCnpjWrapper className="flex align direction-column justify">
            <CpfCnpjInput
              type="text"
              placeholder="CPF ou CNPJ"
              value={form.values.cpfCnpj || ""}
              onChange={(e) => {
                if (
                  e.target.value.length > 18 &&
                  e.target.value.length > form.values.cpfCnpj.length
                )
                  return;
                const formatted = formatCpfCnpj(e.target.value);
                form.handleChange("cpfCnpj", formatted);
              }}
              onBlur={() => form.validate()}
            />
            {form.errors.cpfCnpj && (
              <span style={{ color: "#ff3333" }}>{form.errors.cpfCnpj[0]}</span>
            )}
            <span>
              Não armazenamos seu CPF ou CNPJ. Essa informação será utilizada
              apenas para processar o seu pagamento.
            </span>
          </CpfCnpjWrapper>
          {pixDetails && (
            <>
              <QRCode value={pixDetails.copyPasteCode} />

              <Button
                action={copyPixCode}
                text={copied ? "Copiado!" : "Copiar código Pix"}
                $NoBgColor={false}
                style={{ marginTop: "1rem" }}
              />
            </>
          )}
          {!pixDetails && (
            <Button
              action={async () => {
                if (!pixDetails) await generatePix();
              }}
              text={"Gerar código"}
              $NoBgColor={pixDetails ? true : false}
            />
          )}
        </PaymentInfo>
        <div
          style={{
            marginTop: "2rem",
            textAlign: "center",
            width: "30rem",
            color: "#555",
            fontSize: "1.2rem",
          }}
        >
          Seu pagamento será processado de forma segura pelo{" "}
          <span
            style={{
              fontWeight: "bold",
              textDecoration: "underline",
              color: "#d97706",
            }}
          >
            EfíBank
          </span>
          .
        </div>
      </main>
      <Help />
    </Container>
  );
}
